import { Edit, Add, Delete } from '@mui/icons-material';
import { useMediaQuery, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import { Button as SMButton, Pagination, SearchSection, Table } from '../../../../../components';
import { useSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import { MobileTable } from '../../../../../components/table/MobileTable';
import { getApplicationRoles, deleteApplicationRole } from '../../../../../fetch';
import { IResponse, IApplicationRole } from '../../../../../models';
import { AppRolesFilters } from './app-roles-filters';

export const ManageAppRoles = () => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  //   const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [sortDirection, setSortDirection] = useState<{
    name?: 'Asc' | 'Desc';
    value?: 'Asc' | 'Desc';
  }>({
    name: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('Name');

  const [filters, setFilters] = useState<{
    search?: string;
  }>({});
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const {
    isLoading,
    data: applicationRoles,
    refetch: refetchApplicationRoles
  } = useQuery<IResponse<IApplicationRole[]>, Error>(
    ['getApplicationRoles', page, perPage, selectedSort, sortDirection, filters],
    () =>
      getApplicationRoles({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleDelete = async (id: number) => {
    const result = window.confirm('Are you sure you want to delete this role?');
    if (result) {
      setDeleting(true);
      try {
        await deleteApplicationRole(id);
        refetchApplicationRoles();
        enqueueSnackbar('Role Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error, unable to delete role, please try again', {
          variant: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'systemDisplayName',
      isServerSorted: selectedSort === 'name',
      isServerSortedDesc: sortDirection.name === 'Desc',
      handleClickColumn: () => handleClickColumn('name')
    },
    {
      Header: 'Description',
      accessor: 'systemDescription',
      sort: false
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,
      overrideWidth: 200,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IApplicationRole } };
      }) => {
        return (
          <div className={classes.actionButtons}>
            <SMButton
              id="edit-framer"
              type="button"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEdit(original.applicationRoleId)}
            >
              Edit
            </SMButton>
            <SMButton
              id="delete"
              disabled={isDeleting}
              className={classes.deleteButton}
              startIcon={<Delete />}
              onClick={() => {
                handleDelete(original.applicationRoleId);
              }}
            >
              Delete
            </SMButton>
          </div>
        );
      }
    }
  ];

  const handleEdit = async (id: number) => {
    history.push(
      `/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles/${id}`
    );
  };

  return (
    <Box paddingTop={1}>
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        justifyContent="space-between"
        marginBottom={1}
      >
        <Box flexGrow={1}>
          <AppRolesFilters
            isLoading={isLoading}
            applyFilters={(clearFilters?: boolean) => {
              if (clearFilters) {
                setFilters({
                  search: ''
                });
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  search: searchValue
                });
              }
            }}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setSearchedValue={setSearchedValue}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchedValue(val);
              setFilters({
                ...filters,
                search: val
              });
            }}
            hasAppliedFilters={hasAppliedFilters}
            setHasAppliedFilters={setHasAppliedFilters}
          />
          {searchedValue && (
            <SearchSection
              searchedValue={searchedValue}
              handleClearSearch={() => {
                setSearchValue('');
                setSearchedValue('');
                setHasAppliedFilters(false);
                setPage(0);
                setFilters({
                  search: ''
                });
              }}
            />
          )}
        </Box>
        <Box marginTop={isMobile ? 0 : '-20px'} marginLeft={isMobile ? 0 : 2}>
          <SMButton
            id="create-role"
            startIcon={<Add />}
            onClick={() =>
              history.push(
                '/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles/add-role'
              )
            }
            color="primary"
            variant="contained"
            className={classes.addRoleButton}
          >
            Add Role
          </SMButton>
        </Box>
      </Box>
      <Table
        columns={columns}
        data={applicationRoles?.records || []}
        isLoading={isLoading}
        hidePagination
        ResponsiveComponent={isMobile ? MobileTable : undefined}
        mobileProps={{
          fields: columns
            .filter(col => col.Header)
            .map(col => ({
              name: col.Header,
              accessor: col.accessor,
              Cell: col?.Cell
            })),
          handleEdit: (val: IApplicationRole) => handleEdit(val.applicationRoleId),
          handleDelete: (val: IApplicationRole) => handleDelete(val.applicationRoleId)
        }}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  tableWrapper: {
    paddingTop: theme.spacing(1)
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  link: {
    textDecoration: 'none'
  },
  addRoleButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  }
}));
